import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";

import Layout from "../components/Layout";
import {
  IntentConversionRateChart,
  Section,
  SuccessAndAcceptanceRates,
  useInsightsViewAnalytics,
  PaymentPerformanceChart,
  TotalDeclinesChart,
  TopDeclineCodesChart,
  RevenueChart,
  AnalyticsMetricCard,
  RecoveryRateCard,
  BrandDistributionChart,
  ChartsProvider,
  TotalMetrics,
  useReportsFilters,
  useDefaultDashboardFilters,
} from "../features/insights";
import { useFeatureFlags } from "../hooks/query";
import { PageHeader } from "../components/PageHeading";
import { DatadogAnalytics } from "../features/insights/components/DatadogAnalytics";
import { useScrollSpy } from "../hooks/useScrollSpy";
import { Tabs, cn } from "ui";
import { useStickyBorder } from "../features/insights/components/useStickyBorder";
import { Filters } from "../components/newFilters";
import { AlertsBanner } from "../features/alerts/components/AlertsBanner";
import { useUserContext } from "../contexts/UserProvider";

const OLD_SECTION_TITLES: Record<string, string> = {
  overview: "Overview of your payments",
  revenue: "Revenue Overview",
  declines: "Decline overview",
  recovery: "Recovery overview",
  brands: "Brands overview",
};
function OldDashboardMetrics() {
  const { featureFlags } = useFeatureFlags();

  // Refs for section scrolling
  const overviewSectionRef = useRef<HTMLElement>(null);
  const revenueSectionRef = useRef<HTMLDivElement>(null);
  const declinesSectionRef = useRef<HTMLDivElement>(null);
  const recoverySectionRef = useRef<HTMLDivElement>(null);
  const brandsSectionRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<string>("overview");
  const [currentSectionTitle, setCurrentSectionTitle] = useState<string>(
    OLD_SECTION_TITLES.overview
  );
  const [isScrollSpyEnabled, setIsScrollSpyEnabled] = useState(true);

  const handleTabClick = useCallback((tab: string) => {
    setActiveTab(tab);
    setCurrentSectionTitle(OLD_SECTION_TITLES[tab]);
    setIsScrollSpyEnabled(false);

    const sectionMap: Record<
      string,
      React.RefObject<HTMLElement | HTMLDivElement>
    > = {
      overview: overviewSectionRef,
      revenue: revenueSectionRef,
      declines: declinesSectionRef,
      recovery: recoverySectionRef,
      brands: brandsSectionRef,
    };

    const targetRef = sectionMap[tab];
    if (targetRef?.current) {
      targetRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    setTimeout(() => {
      setIsScrollSpyEnabled(true);
    }, 800);
  }, []);

  // Memoize section refs array for scroll spy
  const sectionRefsArray = useMemo(
    () => [
      { id: "overview", ref: overviewSectionRef },
      { id: "revenue", ref: revenueSectionRef },
      { id: "declines", ref: declinesSectionRef },
      { id: "recovery", ref: recoverySectionRef },
      { id: "brands", ref: brandsSectionRef },
    ],
    []
  );

  useScrollSpy({
    sectionRefs: sectionRefsArray,
    onActiveChange: (activeId) => {
      setActiveTab(activeId);
      setCurrentSectionTitle(OLD_SECTION_TITLES[activeId]);
    },
    rootMargin: "-180px 0px -50% 0px",
    enabled: featureFlags.datadog_dashboard_analytics && isScrollSpyEnabled,
  });

  useInsightsViewAnalytics({ eventName: "home_dashboard_viewed" });

  return (
    <Layout metaTitle="Home">
      <PageHeader
        title="Home"
        description="Get access to an overview of your payment hub in one place."
        withoutBorder={featureFlags.datadog_dashboard_analytics}
      />

      <SuccessAndAcceptanceRates
        ref={overviewSectionRef}
        activeTab={activeTab}
        onTabChange={handleTabClick}
        sectionTitle={currentSectionTitle}
      />

      <DatadogAnalytics
        revenueSectionRef={revenueSectionRef}
        declinesSectionRef={declinesSectionRef}
        recoverySectionRef={recoverySectionRef}
        brandsSectionRef={brandsSectionRef}
      />
    </Layout>
  );
}

// --------------------------------------------------------

const SECTION_TITLES = {
  health: "Intent health",
  payment: "Payment",
  declines: "Declines",
  revenue: "Revenue",
  recovery: "Recovery",
  brands: "Brands",
} satisfies Record<string, string>;

function DashboardMetrics() {
  const { featureFlags } = useFeatureFlags();
  const { isMoneyHashUser } = useUserContext();

  // Refs for section scrolling
  const healthSectionRef = useRef<HTMLDivElement>(null);
  const paymentSectionRef = useRef<HTMLDivElement>(null);
  const declinesSectionRef = useRef<HTMLDivElement>(null);
  const revenueSectionRef = useRef<HTMLDivElement>(null);
  const recoverySectionRef = useRef<HTMLDivElement>(null);
  const brandsSectionRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] =
    useState<keyof typeof SECTION_TITLES>("health");
  const [isScrollSpyEnabled, setIsScrollSpyEnabled] = useState(true);

  useDefaultDashboardFilters();

  const { filters: allFilters } = useReportsFilters();
  const filters = useMemo(
    () =>
      allFilters.filter(
        ({ field_name }) => !["method", "provider"].includes(field_name)
      ),
    [allFilters]
  );

  const handleTabClick = useCallback((tab: keyof typeof SECTION_TITLES) => {
    setActiveTab(tab);
    setIsScrollSpyEnabled(false);

    const sectionMap: Record<
      keyof typeof SECTION_TITLES,
      React.RefObject<HTMLDivElement>
    > = {
      health: healthSectionRef,
      payment: paymentSectionRef,
      declines: declinesSectionRef,
      revenue: revenueSectionRef,
      recovery: recoverySectionRef,
      brands: brandsSectionRef,
    };

    const targetRef = sectionMap[tab];
    if (targetRef?.current) {
      targetRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    setTimeout(() => {
      setIsScrollSpyEnabled(true);
    }, 800);
  }, []);

  // Memoize section refs array for scroll spy
  const sectionRefsArray = useMemo(
    () => [
      { id: "health", ref: healthSectionRef },
      { id: "payment", ref: paymentSectionRef },
      { id: "declines", ref: declinesSectionRef },
      { id: "revenue", ref: revenueSectionRef },
      { id: "recovery", ref: recoverySectionRef },
      { id: "brands", ref: brandsSectionRef },
    ],
    []
  );

  useScrollSpy({
    sectionRefs: sectionRefsArray,
    onActiveChange: (activeId) => {
      setActiveTab(activeId as keyof typeof SECTION_TITLES);
    },
    rootMargin: "-180px 0px -50% 0px",
    enabled: isScrollSpyEnabled,
  });

  useInsightsViewAnalytics({ eventName: "home_dashboard_viewed" });

  const { sentinelRef, isStuck } = useStickyBorder();

  return (
    <Layout metaTitle="Home">
      <PageHeader
        title="Home"
        description="Get access to an overview of your payment hub in one place."
        withoutBorder
      />

      <div ref={sentinelRef} />

      <div
        className={cn(
          "sticky top-0 z-10  border-b border-transparent bg-foundations-default",
          isStuck && "border-foundations-subtle"
        )}
      >
        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            handleTabClick(value as keyof typeof SECTION_TITLES)
          }
        >
          <Tabs.List>
            {Object.entries(SECTION_TITLES).map(([value, label]) => (
              <Tabs.Tab key={value} value={value}>
                {label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>

        <div className="flex flex-col justify-center py-4">
          <Filters
            leftSlot={
              <h2 className="flex-1 text-lg font-medium text-foundations-boldest">
                Overview of your payments
              </h2>
            }
            filters={filters}
            dependantFilter={
              isMoneyHashUser
                ? {
                    name: "account",
                    filterBy: "organization",
                  }
                : undefined
            }
            recentStoreKey="home-dashboard-filters"
            filtersFor="dashboard"
            dateRangeMode="finiteIntervalDates"
          />
        </div>
      </div>

      {featureFlags?.alerts && (
        <div className="mt-2">
          <AlertsBanner />
        </div>
      )}

      <ChartsProvider>
        <div className="flex flex-col gap-8">
          <Section className={featureFlags.alerts ? undefined : "mt-4"}>
            <Section.Title>Overview</Section.Title>
            <TotalMetrics />
          </Section>

          <Section ref={healthSectionRef} id="health-section">
            <Section.Title>{SECTION_TITLES.health}</Section.Title>
            <IntentConversionRateChart />
          </Section>

          <Section ref={paymentSectionRef} id="payment-section">
            <Section.Title viewMoreLink="/reports/payment-performance">
              {SECTION_TITLES.payment}
            </Section.Title>
            <PaymentPerformanceChart />
          </Section>

          <Section ref={declinesSectionRef} id="declines-section">
            <Section.Title viewMoreLink="/reports/declines">
              {SECTION_TITLES.declines}
            </Section.Title>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <TotalDeclinesChart />
              </div>
              <div className="lg:col-span-4">
                <TopDeclineCodesChart />
              </div>
            </div>
          </Section>

          <Section ref={revenueSectionRef} id="revenue-section">
            <Section.Title viewMoreLink="/reports/volume-revenue">
              {SECTION_TITLES.revenue}
            </Section.Title>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <RevenueChart />
              </div>
              <div className="lg:col-span-4">
                <AnalyticsMetricCard
                  chartType="CAPTURED_REVENUE_TOTAL"
                  title="Captured revenue"
                  description="Total value of successfully captured payments in the selected period in USD."
                  format="currency"
                  className="h-full"
                />
              </div>
            </div>
          </Section>

          <Section ref={recoverySectionRef} id="recovery-section">
            <Section.Title learnMoreLink="https://moneyhash-knowledge-base.help.usepylon.com/articles/5343336667-recovery-statistics-in-moneyhash-home-dashboard">
              {SECTION_TITLES.recovery}
            </Section.Title>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <RecoveryRateCard
                chartType="RECOVERY_RATE"
                title="Recovery rate"
                description="Recovered intents ÷ all retried intents. Answers: when we retry a failed transaction, what percentage is successful."
              />
              <RecoveryRateCard
                chartType="OVERALL_SOFT_DECLINE_RECOVERY"
                title="Overall soft decline recovery"
                description="Recovered ÷ every first-try soft decline, includes soft declines we never retried. Answers: from all soft declines, what percentage did we recover."
              />
              <RecoveryRateCard
                chartType="SOFT_DECLINE_COVERAGE"
                title="Soft decline coverage"
                description="Retried ÷ every first-try soft decline. Answers: how many recoverable declines did we actually retry? Low % means recovery can be configured better."
              />
            </div>
          </Section>

          <Section ref={brandsSectionRef} id="brands-section">
            <Section.Title viewMoreLink="/reports/brands">
              {SECTION_TITLES.brands}
            </Section.Title>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
              <BrandDistributionChart />
            </div>
          </Section>
        </div>
      </ChartsProvider>
    </Layout>
  );
}

export default function DashboardWrapper() {
  const { featureFlags } = useFeatureFlags();

  useEffect(() => {
    window.Appcues?.track("analytics_dashboard.viewed");
  }, []);

  if (featureFlags.dashboard_analytics_reports) {
    return <DashboardMetrics />;
  }

  return <OldDashboardMetrics />;
}
